import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";
import { GALLERY_FILTERS, PROJECTS } from "@/lib/site-data";

const ease = [0.22, 1, 0.36, 1];

export default function Gallery() {
  const [filter, setFilter] = useState("tous");
  const [active, setActive] = useState(null);
  const filtered = filter === "tous" ? PROJECTS : PROJECTS.filter((p) => p.cat === filter);

  return (
    <section id="realisations" data-testid="gallery-section" className="relative py-28 md:py-36">
      <div className="mx-auto max-w-7xl px-6">
        <SectionHeading
          eyebrow="Réalisations"
          title="Des toits qui racontent Bordeaux"
          copy="Échoppes, hôtels particuliers, maisons de ville : un aperçu de nos derniers chantiers en Gironde."
        />

        <div className="mt-12 flex flex-wrap gap-2">
          {GALLERY_FILTERS.map((f) => (
            <button
              key={f.id}
              data-testid={`gallery-filter-${f.id}`}
              onClick={() => setFilter(f.id)}
              className={`rounded-full border px-5 py-2 text-xs font-semibold uppercase tracking-widest transition-colors duration-300 ${
                filter === f.id
                  ? "border-terra bg-terra text-white"
                  : "border-white/10 text-slate-400 hover:border-white/30 hover:text-slate-200"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <motion.div layout className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <AnimatePresence mode="popLayout">
            {filtered.map((p, i) => (
              <motion.button
                layout
                key={p.id}
                data-testid={`gallery-item-${p.id}`}
                onClick={() => setActive(p)}
                initial={{ opacity: 0, scale: 0.94 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.94 }}
                transition={{ duration: 0.45, ease }}
                className={`group relative overflow-hidden border border-white/5 text-left ${
                  i % 4 === 0 || i % 4 === 3 ? "aspect-[4/5]" : "aspect-square"
                }`}
              >
                <img
                  src={p.img}
                  alt={p.title}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/25 to-transparent opacity-90 transition-opacity duration-500 group-hover:opacity-100" />
                <div className="absolute inset-x-0 bottom-0 border-t border-white/15 bg-black/70 p-4 backdrop-blur-md">
                  <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-terra">{p.lieu}</p>
                  <p className="mt-1 font-serif text-xl font-semibold text-white">{p.title}</p>
                </div>
              </motion.button>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="gallery-lightbox"
            onClick={() => setActive(null)}
            className="fixed inset-0 z-[80] flex items-center justify-center bg-black/90 p-6 backdrop-blur-sm"
          >
            <motion.figure
              initial={{ scale: 0.92, y: 24 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 12 }}
              transition={{ duration: 0.35, ease }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-4xl"
            >
              <img src={active.img} alt={active.title} className="max-h-[72vh] w-full object-contain" />
              <figcaption className="mt-5 flex items-end justify-between gap-4">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-terra">{active.lieu}</p>
                  <p className="mt-1 font-serif text-2xl text-white">{active.title}</p>
                </div>
                <button
                  data-testid="gallery-lightbox-close"
                  onClick={() => setActive(null)}
                  aria-label="Fermer"
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-white/15 text-slate-200 transition-colors hover:border-terra hover:text-terra"
                >
                  <X className="h-5 w-5" />
                </button>
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
